import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Blackpool\'s AMBER DfT rating strengthen my claim?',
    answer: 'It can, but no rating guarantees success. The Department for Transport rates Blackpool AMBER overall and on condition, with GREEN on spend and best practice. That AMBER condition scorecard reflects network-level deterioration — including 65.8% of A-roads and 63.2% of B and C roads in amber condition at the 2024/25 survey. Section 58 still turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired.',
  },
  {
    question: 'Blackpool claims 91–97% preventative maintenance — isn\'t that good?',
    answer: 'The percentages look strong on paper, but condition data tells a different story. Good-condition A-roads fell from 42.1% to 30.2% between 2020/21 and 2024/25, while amber A-roads rose from 52.5% to 65.8%. B and C roads show the same pattern. High preventative shares are not proof that individual defects were prevented on your road — especially when the council still expects to fill around 3,000 potholes in 2025/26.',
  },
  {
    question: 'Blackpool has GREEN spend and best-practice scorecards — can I still claim?',
    answer: 'Yes. GREEN spend reflects projected 2025/26 capital spend of £6.0m against a DfT allocation of £6.0m — a 217% rise from 2024/25, including £2.366m from the Highways Maintenance Allocation and £3.634m from the Local Transport Grant. GREEN best practice reflects published innovation plans. Neither scorecard proves the individual pothole was known and repaired within inspection intervals on your street.',
  },
  {
    question: 'What if my pothole was on a residential unclassified road?',
    answer: 'Unclassified roads make up 451km — 83% of Blackpool\'s 542km carriageway network. At the 2024/25 survey, 6.7% of U-roads were in red condition — roughly 30km of residential streets. The council surveys U-roads annually using detailed visual inspection video technology, but a network-level percentage does not prove your specific defect was caught in time. Prior reports and photographic evidence of defect age matter more.',
  },
  {
    question: 'Why did pothole repairs drop 41% in 2021/22 then climb back?',
    answer: 'Blackpool filled 3,103 potholes in 2020/21, then 1,837 in 2021/22 — a 40.8% fall while reactive spending was estimated at just 6%. Repairs climbed back to 2,941 in 2023/24 and 3,009 in 2024/25. That pattern shows reactive workload is volatile — and that the network did not become 40% better overnight. A-road amber condition actually worsened over the same period.',
  },
  {
    question: 'Blackpool\'s 2025/26 budget has tripled — does that affect my claim?',
    answer: 'Not for past incidents. If your damage occurred before the 2025/26 funding boost, your claim is judged against the maintenance standards and resources in effect when the pothole formed. The jump from £1.892m to £6.0m capital allocation implicitly acknowledges prior funding was insufficient to reverse accumulated deterioration — with 65.8% of A-roads already in amber condition.',
  },
  {
    question: 'How do I report a pothole to Blackpool Council?',
    answer: 'Report potholes and footway defects via Blackpool\'s online form at blackpool.gov.uk. The council asks you to report defects of 40mm or greater on roads and 25mm or greater on footways. Prior reports of the same defect strengthen a claim by demonstrating the council had notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function BlackpoolCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Blackpool Pothole Claims | 3,009 Repairs as A-Roads Hit 65.8% Amber | Fixtyer"
        description="Blackpool Council earns AMBER DfT condition ratings despite GREEN spend and best practice. Its transparency report records 3,009 pothole repairs in 2024/25 while good-condition A-roads fell 28% since 2020/21. See what this means for your Section 58 claim."
        canonical="/councils/blackpool"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Blackpool', url: '/councils/blackpool' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Blackpool Council"
        councilSlug="blackpool"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/blackpool"
        headline="Blackpool Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Blackpool</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            3,009 Pothole Repairs in 2024/25 While 65.8% of A-Roads Sit in Amber
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Blackpool earns <strong className="text-amber-400">AMBER</strong> DfT scorecards for overall performance
            and road condition, with <strong className="text-emerald-400">GREEN</strong> on spend and best practice.
            Its own transparency report records <strong className="text-rose-400">3,009 pothole repairs in 2024/25</strong>,
            claims <strong className="text-amber-400">91% preventative maintenance</strong>, yet good-condition A-roads
            fell from 42.1% to 30.2% since 2020/21. Eighty-three per cent of the network is residential. Section 58
            still turns on your specific defect.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">£6.0m</div>
            <div className="text-white font-medium mt-2">Projected capital spend 2025/26</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from £1.892m in 2024/25 — a 217% increase — while the council still expects ~3,000 pothole fills
              and only resurfaced approximately 6km of its 542km network last year.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The DfT&apos;s Verdict
          </h2>
          <p className="text-surface-600 mb-8">
            Four scorecards from the Department for Transport — December 2025 transparency data
          </p>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Blackpool Council DfT Road Maintenance Ratings 2025 to 2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left font-semibold text-brand-900">Scorecard</th>
                  <th className="px-6 py-4 text-center font-semibold text-brand-900">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Condition</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Spend</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Wider Best Practice</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
            <p className="text-amber-900 text-sm">
              <strong>What the split means:</strong> GREEN spend and best practice show Blackpool is investing
              heavily and publishing credible innovation plans — thermal recycling, digital asset tools and permanent
              pothole repair techniques. But the AMBER condition scorecard reflects what surveys actually measure:
              two-thirds of classified roads in amber, good-condition share falling, and reactive pothole workload
              back at 2020/21 levels despite 91% preventative spend claimed in 2024/25.
            </p>
          </div>

          <p className="text-sm text-surface-500">
            Source:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>
          </p>
        </div>
      </section>

      {/* Network scale */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            542km of Roads — Mostly Residential
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Blackpool&apos;s December 2025 transparency report — where pothole claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">542km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
              <div className="text-xs text-surface-500 mt-1">337 miles</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">451km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">83% of the network</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">55km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">36km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Blackpool highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">891km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Blackpool and Fleetwood Tramway</td>
                  <td className="px-6 py-4 text-center font-medium">18.2km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Bridges, footbridges and structures</td>
                  <td className="px-6 py-4 text-center font-medium">43 structures</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Streetlights</td>
                  <td className="px-6 py-4 text-center font-medium">15,640 columns</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Resurfaced in 2024/25</td>
                  <td className="px-6 py-4 text-center font-medium text-amber-600">~6km (1.1% of network)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="We aim to keep all parts of our network safe and in good condition, using data and local knowledge to prioritise where maintenance is needed most."
            source="Blackpool Council Local Highways Maintenance Transparency Report (December 2025)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What AMBER Condition Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Annual video imagery surveys — A, B/C and U-roads all measured each year
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Methodology caveat:</strong> Blackpool assesses road condition using detailed visual inspection
              video advanced imagery technology, categorising surfaces as green (no immediate work), amber (may need
              maintenance soon) or red (urgent maintenance). From 2026/27, all councils will move to PAS 2161 with
              five condition bands instead of three. Survey snapshots reflect condition at the time of measurement —
              not necessarily the state of your road on the day of your incident.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (55km) — surveyed annually</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">5.3%</td>
                    <td className="px-4 py-2 text-center">52.5%</td>
                    <td className="px-4 py-2 text-center">42.1%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">6.5%</td>
                    <td className="px-4 py-2 text-center">58.9%</td>
                    <td className="px-4 py-2 text-center">34.4%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">3.9%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">65.8%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">30.2%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Red A-road condition improved — but amber rose from 52.5% to 65.8% (+25% relative) while
              good-condition roads fell from 42.1% to 30.2% (−28% relative). The network is shifting from green
              into &ldquo;needs maintenance soon&rdquo; territory faster than it is being resurfaced.
            </p>
          </div>

          {/* B&C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (36km) — same deterioration pattern</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">9.8%</td>
                    <td className="px-4 py-2 text-center">50.1%</td>
                    <td className="px-4 py-2 text-center">40.1%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">9.3%</td>
                    <td className="px-4 py-2 text-center">51.7%</td>
                    <td className="px-4 py-2 text-center">38.8%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold">6.0%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">63.2%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">30.7%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 text-sm">
              B and C roads mirror A-roads: amber share up from 50.1% to 63.2% (+26% relative), good condition
              down from 40.1% to 30.7%.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-brand-900 mb-4">Unclassified roads (451km) — where most claims start</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red (urgent)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">8.4%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">9.9%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">6.7%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              U-road red condition improved from a 2022/23 peak of 9.9% to 6.7% in 2024/25 — but at 451km of
              residential network, 6.7% red still represents approximately 30km of roads requiring urgent maintenance.
              Blackpool publishes red percentages only for U-roads, not full amber/green breakdowns.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs and prevention paradox */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Prevention Paradox
          </h2>
          <p className="text-surface-600 mb-8">
            High preventative percentages on paper — pothole workload and amber roads both rising
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Blackpool pothole repairs and maintenance split 2020 to 2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">3,103</td>
                  <td className="px-4 py-3 text-center">86%</td>
                  <td className="px-4 py-3 text-center">14%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">1,837</td>
                  <td className="px-4 py-3 text-center">94%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">2,941</td>
                  <td className="px-4 py-3 text-center">91%</td>
                  <td className="px-4 py-3 text-center">9%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-4 py-3 font-mono font-bold">2024/25</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-600">3,009</td>
                  <td className="px-4 py-3 text-center">91%</td>
                  <td className="px-4 py-3 text-center">9%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-3 font-bold">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center font-bold">~3,000</td>
                  <td className="px-4 py-3 text-center text-emerald-600">97%</td>
                  <td className="px-4 py-3 text-center">3%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">The 2021/22 drop</h3>
              <p className="text-rose-800 text-sm">
                Pothole repairs fell 40.8% to 1,837 while reactive spending was estimated at just 6%. That was a
                budget allocation choice — not evidence roads had improved. Amber A-road share was already climbing.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Back to baseline, worse condition</h3>
              <p className="text-amber-800 text-sm">
                Repairs climbed back to 3,009 in 2024/25 — near the 2020/21 baseline — while 65.8% of A-roads are
                now amber versus 52.5% five years earlier. Reactive workload returned; underlying deterioration did not reverse.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="We determine the split between preventative and reactive works based on regular condition surveys and historical data. Our goal is to increase preventative maintenance where possible as this helps reduce future costs and disruption."
            source="Blackpool Council Local Highways Maintenance Transparency Report (December 2025)"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            GREEN spend — but years of modest capital before the 2025/26 boost
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Blackpool highway maintenance spending 2020 to 2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">km resurfaced</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-emerald-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">6,000</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">6,000</td>
                  <td className="px-4 py-3 text-center">600</td>
                  <td className="px-4 py-3 text-center font-bold">~17</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">1,892</td>
                  <td className="px-4 py-3 text-center">1,892</td>
                  <td className="px-4 py-3 text-center">510</td>
                  <td className="px-4 py-3 text-center font-bold">~6</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">1,668</td>
                  <td className="px-4 py-3 text-center">1,668</td>
                  <td className="px-4 py-3 text-center">461</td>
                  <td className="px-4 py-3 text-center text-brand-400">—</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">1,248</td>
                  <td className="px-4 py-3 text-center">1,248</td>
                  <td className="px-4 py-3 text-center">445</td>
                  <td className="px-4 py-3 text-center text-brand-400">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is GREEN</h3>
              <p className="text-brand-200 text-sm">
                2025/26 capital rises to £6.0m — including £2.366m from the Highways Maintenance Allocation and
                £3.634m from the Local Transport Grant — with approximately 17km of resurfacing planned. That is a
                credible investment response after years at £1.2m–£1.9m annually.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why claims still happen</h3>
              <p className="text-brand-200 text-sm">
                Even at 17km resurfacing, Blackpool would treat roughly 3.1% of its 542km network in a single year.
                The remaining 97% relies on patching, inspections and the condition data showing two-thirds of
                classified roads already in amber. One funding boost does not erase years of managed deterioration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 58 and claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an Amber-Rated Seaside Borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Blackpool invests in innovation — but condition data shows managed decline on classified roads
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
              <h3 className="font-semibold text-emerald-900 mb-4">What Works In The Council&apos;s Favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend and best-practice DfT scorecards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual video surveys on A, B/C and U-roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £6.0m capital planned for 2025/26 — 217% rise
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Permanent pothole repair techniques and thermal recycling published
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road red condition improved from 9.9% peak to 6.7%
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a prepared Section 58 defence citing GREEN scorecards and annual survey coverage.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER overall and condition DfT ratings
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 65.8% of A-roads and 63.2% of B/C roads in amber condition
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Good-condition A-roads down 28% relative since 2020/21
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 3,009 pothole repairs in 2024/25 despite 91% preventative spend
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Only ~6km resurfaced in 2024/25 — 1.1% of the network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 451km of U-roads — 83% of network — with red-only published data
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Blackpool must
              maintain public highways. To defend a claim under <strong className="text-white">Section 58</strong>,
              it must show a reasonable system for inspecting and repairing the specific defect — not just publish
              GREEN spend scorecards and high preventative percentages.
            </p>
            <ul className="text-brand-300 space-y-2 mb-6 text-sm">
              <li>• Was your defect visible and reportable before your incident?</li>
              <li>• Did prior reports (FixMyStreet, council online form) give actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the inspection interval?</li>
              <li>• On a U-road, can the council prove timely action when ~30km of residential network is in red condition?</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from Blackpool&apos;s
              own transparency data where it helps you — without pretending the council fails on every measure.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to Blackpool Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Blackpool asks you to report potholes of
            40mm or greater on roads and 25mm or greater on footways. Do this before claiming — and tell us when
            you reported it so we can reference it in your pack.
          </p>
          <a
            href="https://www.blackpool.gov.uk/Residents/Parking-roads-and-transport/Roadworks-and-road-maintenance/Report-a-pothole-road-or-footpath-problem.aspx"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a pothole — blackpool.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Blackpool?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            An amber-rated network with a prevention paradox demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No 65.8% A-road amber data cited</li>
                <li>• No prior-report search</li>
                <li>• No prevention-paradox argument</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ AMBER condition deterioration documented</li>
                <li>✅ 3,009 pothole repairs in 2024/25 cited</li>
                <li>✅ 91% preventative vs rising amber roads argued</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Blackpool</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=blackpool"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Blackpool Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.blackpool.gov.uk/Your-Council/Transparency-and-open-data/Highway-maintenance-funding.aspx"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Blackpool Council Local Highways Maintenance Transparency Report (December 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
